import { Image as ImageIcon, RotateCcw, Trash2 } from "lucide-react";
import { formatDate } from "@ckgo2/shared/utils";
import type { MediaResponse } from "@ckgo2/shared/types";

export interface DeparturePhoto {
  id: string;
  media: MediaResponse | null;
  caption: string | null;
  photoDate: string;
  guideId: string | null;
  deletedAt: string | null;
  createdAt: string;
}

/**
 * Guide-shared daily trip photos with office moderation (Area 5a) — each photo can be hidden
 * (and restored). Renders nothing when there are no photos. `onModerate(photoId, restore)` flips
 * the soft-hide state.
 */
export function DeparturePhotosPanel({
  photos,
  onModerate,
}: {
  photos: DeparturePhoto[];
  onModerate: (photoId: string, restore: boolean) => void;
}) {
  if (photos.length === 0) return null;
  return (
    <div className="card">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <ImageIcon className="h-5 w-5" /> Trip photos
        <span className="text-sm font-normal text-gray-400">({photos.length})</span>
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {photos.map((p) => (
          <div
            key={p.id}
            className={`group relative overflow-hidden rounded-lg border border-gray-100 dark:border-gray-700 ${
              p.deletedAt ? "opacity-40" : ""
            }`}
          >
            {p.media ? (
              <img
                src={p.media.thumbUrl}
                alt={p.caption || "Trip photo"}
                className="aspect-square w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex aspect-square w-full items-center justify-center bg-gray-100 text-xs text-gray-400 dark:bg-gray-800">
                Processing…
              </div>
            )}
            <div className="absolute right-1 top-1 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                type="button"
                onClick={() => onModerate(p.id, !!p.deletedAt)}
                title={p.deletedAt ? "Restore" : "Hide"}
                className="rounded bg-black/60 p-1 text-white hover:bg-black/80"
              >
                {p.deletedAt ? (
                  <RotateCcw className="h-3.5 w-3.5" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
            {(p.caption || p.photoDate) && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5">
                <p className="truncate text-[11px] text-white">
                  {p.caption || formatDate(p.photoDate)}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
